

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField,
  CircularProgress, Alert, Divider, IconButton, Tooltip
} from '@mui/material';
import {
  Schedule, Refresh,
  ExpandMore, ExpandLess, Send, Close, Inbox
} from '@mui/icons-material';
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { apiRequest } from '../../fetchSetup';

// ── Palette ───────────────────────────────────────────────────────────────────
const STATUS_META = {
  pending:            { label: 'Pending',          bgcolor: '#fff7ed', color: '#c2410c', border: '#fed7aa', dot: '#f97316' },
  noted:              { label: 'Noted',             bgcolor: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6' },
  accepted:           { label: 'Accepted',          bgcolor: '#f0fdf4', color: '#15803d', border: '#bbf7d0', dot: '#22c55e' },
  rejected:           { label: 'Rejected',          bgcolor: '#fef2f2', color: '#b91c1c', border: '#fecaca', dot: '#ef4444' },
  partially_accepted: { label: 'Partially accepted', bgcolor: '#fefce8', color: '#a16207', border: '#fef08a', dot: '#eab308' },
  added:              { label: 'Added',             bgcolor: '#f0fdf4', color: '#15803d', border: '#bbf7d0', dot: '#22c55e' },
};

const ITEM_COLORS = {
  pending:  { bgcolor: '#f1f5f9', color: '#475569' },
  noted:    { bgcolor: '#eff6ff', color: '#1d4ed8' },
  added:    { bgcolor: '#f0fdf4', color: '#15803d' },
  rejected: { bgcolor: '#fef2f2', color: '#b91c1c' },
};
const RESPONSE_OPTIONS = [
  {
    value: "available",
    label: "The requested medicines are available. You can now place your order."
  },
  {
    value: "partially_available",
    label: "Only few of the requested medicines is available. Please confirm if you'd like to proceed."
  },
  {
    value: "alternative_available",
    label: "The requested medicines are not available, but an equivalent alternative is available."
  },
  {
    value: "out_of_stock",
    label: "The requested medicines is currently out of stock. We'll notify you when it becomes available."
  },
  {
    value: "can_be_procured",
    label: "The medicines is not currently in stock but can be arranged."
  },
  {
    value: "discontinued",
    label: "This medicines has been discontinued by the manufacturer. Please consult your doctor for an alternative."
  },
  {
    value: "prescription_required",
    label: "A valid prescription is required before this medicines can be supplied."
  },
  {
    value: "rejected",
    label: "We are unable to process this medicines request."
  }
];
const COMPLETED = ['noted', 'accepted', 'rejected', 'partially_accepted'];

const AVAILABILITY_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'not_available', label: 'Not available' },
];

// ── Sorting logic: pending first (newest → oldest), then completed (newest → oldest) ──
const sortRequests = (list) => {
  const pending   = list.filter(r => !COMPLETED.includes(r.status))
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const completed = list.filter(r => COMPLETED.includes(r.status))
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return [...pending, ...completed];
};

// ── Friendly date ─────────────────────────────────────────────────────────────
const fmtDate = (iso) =>
  new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

// ── Status chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ status, size = 'small' }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <Chip
      label={m.label}
      size={size}
      sx={{
        bgcolor: m.bgcolor,
        color: m.color,
        border: `1px solid ${m.border}`,
        fontWeight: 600,
        fontSize: size === 'small' ? 11 : 12,
      }}
    />
  );
};

// ── Respond dialog (inline, not MUI Dialog — avoids position:fixed) ───────────
const RespondPanel = ({ req, onCancel, onSuccess }) => {
  const [adminNote,   setAdminNote]   = useState('');
  const [responseType, setResponseType] = useState('');
  const [availability, setAvailability] = useState(() =>
    Object.fromEntries((req.medicines || []).map(medicine => [medicine.item_id, '']))
  );
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');

  const allMedicinesReviewed = (req.medicines || []).every(
    medicine => availability[medicine.item_id]
  );

  const selectedResponse = RESPONSE_OPTIONS.find(option => option.value === responseType);
  const availableItems = (req.medicines || [])
    .filter(medicine => availability[medicine.item_id] === 'available')
    .map(medicine => medicine.item_id);
  const unavailableItems = (req.medicines || [])
    .filter(medicine => availability[medicine.item_id] === 'not_available')
    .map(medicine => medicine.item_id);
  const action = availableItems.length === req.medicines.length
    ? 'accepted'
    : availableItems.length === 0
      ? 'rejected'
      : 'partially_accepted';

  const handleSubmit = async () => {
    if (!allMedicinesReviewed) {
      setError('Please mark each medicine as available or not available.');
      return;
    }

    setSubmitting(true); setError('');
    try {
      const message = [selectedResponse?.label, adminNote.trim()]
        .filter(Boolean)
        .join('\n\n');
      const res = await apiRequest(
        `${process.env.REACT_APP_MEDICINE_API_URL}/admin/medicine-requests/${req.request_id}/respond`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action,
            admin_response: message,
            accepted_items: availableItems,
            rejected_items: unavailableItems,
          }),
        }
      );
      if (!res.ok) throw new Error();
      onSuccess();
    } catch {
      setError('Failed to send response. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
          Respond to patient
        </Typography>
        <IconButton size="small" onClick={onCancel} sx={{ color: '#94a3b8' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Response template (optional)</InputLabel>
        <Select
          value={responseType}
          label="Response template (optional)"
          onChange={(e) => setResponseType(e.target.value)}
        >
          <MenuItem value=""><em>No template</em></MenuItem>
          {RESPONSE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#334155', mb: 1 }}>
        Medicine availability
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {req.medicines.map(medicine => (
          <Box
            key={medicine.item_id}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              p: 1.25, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#1e293b' }}>
                {medicine.medicine_name}
              </Typography>
              {medicine.notes && (
                <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
                  {medicine.notes}
                </Typography>
              )}
            </Box>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Availability</InputLabel>
              <Select
                value={availability[medicine.item_id] || ''}
                label="Availability"
                onChange={event => setAvailability(current => ({
                  ...current,
                  [medicine.item_id]: event.target.value,
                }))}
              >
                <MenuItem value="" disabled><em>Select status</em></MenuItem>
                {AVAILABILITY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ))}
      </Box>
      <TextField
        label="Additional message to patient (optional)"
        value={adminNote}
        onChange={e => setAdminNote(e.target.value)}
        fullWidth
        multiline
        rows={2}
        size="small"
        placeholder="e.g. This medicine will be available within 3 working days"
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={onCancel}
          size="small"
          sx={{ textTransform: 'none', color: '#64748b', flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={submitting || !allMedicinesReviewed}
          startIcon={<Send sx={{ fontSize: 13 }} />}
          sx={{
            bgcolor: '#006d77',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12,
            flex: 2,
            '&:hover': { bgcolor: '#005560' },
          }}
        >
          {submitting ? 'Sending…' : 'Send & notify patient'}
        </Button>
      </Box>
    </Box>
  );
};

// ── Single request card ───────────────────────────────────────────────────────
const RequestCard = ({ req, onRespond, isFirstCompleted }) => {
  const [expanded,    setExpanded]    = useState(false);
  const [responding,  setResponding]  = useState(false);

  const isCompleted = COMPLETED.includes(req.status);
  const sm = STATUS_META[req.status] || STATUS_META.pending;

  return (
    <>
      {/* Divider label between pending and completed sections */}
      {isFirstCompleted && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
          <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0' }} />
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Completed
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0' }} />
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: isCompleted ? '#f1f5f9' : '#e2e8f0',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 1.5,
          opacity: isCompleted ? 0.82 : 1,
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: isCompleted ? 'none' : '0 4px 16px rgba(0,0,0,0.06)' },
        }}
      >
        {/* Status accent bar */}
        <Box sx={{ height: 3, bgcolor: sm.dot }} />

        <Box sx={{ p: 2.5 }}>
          {/* Top row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                  {req.user_email || req.user_phone || 'Anonymous'}
                </Typography>
                <StatusChip status={req.status} />
              </Box>
              <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.4 }}>
                {fmtDate(req.created_at)}
                {' · '}
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  #{req.request_id.slice(0, 8)}
                </Box>
              </Typography>
            </Box>

            <Chip
              label={`${req.medicines_count} item${req.medicines_count !== 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: 11, flexShrink: 0 }}
            />
          </Box>

          {/* Medicine pills */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {req.medicines.map(m => (
              <Chip
                key={m.item_id}
                label={m.medicine_name}
                size="small"
                sx={{ ...ITEM_COLORS[m.status], fontSize: 11, fontWeight: 500 }}
              />
            ))}
          </Box>

          {/* Admin note (if responded) */}
          {isCompleted && req.admin_response && (
            <Box
              sx={{
                mt: 1.5, px: 1.5, py: 1,
                bgcolor: '#f8fafc',
                borderRadius: 1.5,
                borderLeft: '3px solid #e2e8f0',
              }}
            >
              <Typography sx={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                "{req.admin_response}"
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.4 }}>
                Responded {fmtDate(req.responded_at)}
              </Typography>
            </Box>
          )}

          {/* Actions row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Button
              size="small"
              endIcon={expanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
              onClick={() => { setExpanded(p => !p); setResponding(false); }}
              sx={{ textTransform: 'none', color: '#64748b', fontSize: 12, px: 0 }}
            >
              {expanded ? 'Hide details' : 'View details'}
            </Button>

            {!isCompleted && !responding && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Send sx={{ fontSize: 13 }} />}
                onClick={() => { setResponding(true); setExpanded(true); }}
                sx={{
                  bgcolor: '#006d77',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 12,
                  '&:hover': { bgcolor: '#005560' },
                }}
              >
                Respond
              </Button>
            )}
          </Box>
        </Box>

        {/* Expanded detail */}
        {expanded && !responding && (
          <>
            <Divider />
            <Box sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
              {req.medicines.map((m, idx) => (
                <Box
                  key={m.item_id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    py: 1.25,
                    borderBottom: idx < req.medicines.length - 1 ? '1px solid #e2e8f0' : 'none',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                      {idx + 1}. {m.medicine_name}
                    </Typography>
                    {m.notes && (
                      <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.25 }}>
                        {m.notes}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={m.status}
                    size="small"
                    sx={{ ...ITEM_COLORS[m.status], fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}
                  />
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Inline respond panel */}
        {responding && (
          <RespondPanel
            req={req}
            onCancel={() => { setResponding(false); setExpanded(false); }}
            onSuccess={() => { setResponding(false); setExpanded(false); onRespond(); }}
          />
        )}
      </Paper>
    </>
  );
};

// ── Filter tab ────────────────────────────────────────────────────────────────
const FilterTab = ({ label, count, active, onClick }) => (
  <Button
    size="small"
    onClick={onClick}
    variant={active ? 'contained' : 'outlined'}
    sx={{
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 12,
      borderRadius: 5,
      ...(active
        ? { bgcolor: '#006d77', borderColor: '#006d77', color: '#fff', '&:hover': { bgcolor: '#005560' } }
        : { borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#006d77', color: '#006d77' } }
      ),
    }}
  >
    {label}
    {count > 0 && (
      <Box
        component="span"
        sx={{
          ml: 0.75,
          px: 0.75,
          py: 0.1,
          bgcolor: active ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
          color: active ? '#fff' : '#475569',
          borderRadius: 10,
          fontSize: 10,
          fontWeight: 700,
        }}
      >
        {count}
      </Box>
    )}
  </Button>
);

// ── Main panel ────────────────────────────────────────────────────────────────
const AdminMedicineRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');
  const [error,    setError]    = useState('');

  const counts = {
    all:                requests.length,
    pending:            requests.filter(r => !COMPLETED.includes(r.status)).length,
    accepted:           requests.filter(r => r.status === 'accepted').length,
    partially_accepted: requests.filter(r => r.status === 'partially_accepted').length,
    rejected:           requests.filter(r => r.status === 'rejected').length,
  };

  const fetchRequests = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_MEDICINE_API_URL}/admin/medicine-requests`,
        { method: 'GET' }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRequests(data.requests || []);
    } catch {
      setError('Could not load requests. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  // filter then sort (pending first, then completed, both newest→oldest)
  const base     = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const filtered = sortRequests(base);

  // index of first completed card (for divider rendering)
  const firstCompletedIdx = filtered.findIndex(r => COMPLETED.includes(r.status));

  const TABS = [
    { key: 'all',                label: 'All' },
    { key: 'pending',            label: 'Pending' },
    { key: 'accepted',           label: 'Accepted' },
    { key: 'partially_accepted', label: 'Partial' },
    { key: 'rejected',           label: 'Rejected' },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 860, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1e293b" letterSpacing={-0.3}>
            Medicine Requests
          </Typography>
          <Typography fontSize={13} color="text.secondary" mt={0.25}>
            {counts.pending > 0
              ? `${counts.pending} request${counts.pending !== 1 ? 's' : ''} awaiting response`
              : 'All requests have been responded to'}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchRequests}
            disabled={loading}
            sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}
          >
            <Refresh fontSize="small" sx={{ color: '#64748b' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary row */}
      {!loading && !error && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 1.5,
            mb: 3,
          }}
        >
          {[
            { label: 'Total',    value: counts.all,                color: '#1e293b' },
            { label: 'Pending',  value: counts.pending,            color: '#c2410c' },
            { label: 'Accepted', value: counts.accepted,           color: '#15803d' },
            { label: 'Partial',  value: counts.partially_accepted, color: '#a16207' },
            { label: 'Rejected', value: counts.rejected,           color: '#b91c1c' },
          ].map(s => (
            <Box
              key={s.label}
              sx={{
                bgcolor: '#f8fafc',
                border: '1px solid #f1f5f9',
                borderRadius: 2,
                px: 1.5, py: 1.25,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1.1 }}>
                {s.value}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.25 }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {TABS.map(tab => (
          <FilterTab
            key={tab.key}
            label={tab.label}
            count={counts[tab.key] ?? 0}
            active={filter === tab.key}
            onClick={() => setFilter(tab.key)}
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={32} sx={{ color: '#006d77' }} />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Inbox sx={{ fontSize: 52, color: '#cbd5e1' }} />
          <Typography sx={{ mt: 1.5, color: '#64748b', fontWeight: 600, fontSize: 14 }}>
            No {filter === 'all' ? '' : filter.replace('_', ' ')} requests
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: 13, mt: 0.5 }}>
            {filter === 'pending' ? 'You\'re all caught up!' : 'Nothing to show here.'}
          </Typography>
        </Box>
      ) : (
        filtered.map((req, idx) => (
          <RequestCard
            key={req.request_id}
            req={req}
            onRespond={fetchRequests}
            isFirstCompleted={idx === firstCompletedIdx && firstCompletedIdx > 0}
          />
        ))
      )}
    </Box>
  );
};

export default AdminMedicineRequests;
